/* @flow */

import type { $AxiosXHR } from 'axios';
import type { $Configuration } from '../types/Configuration';
import type { $Dealer } from '../types/Dealer';
import type { $DealerFunction } from '../types/Dealer';
import type { $DropdownOptions } from '../types/DropdownOptions';
import type { $Translation } from '../types/Translation';
import DMSConsumer from '../consumers/DMSConsumer';
import FieldKeysGeneric from '../constants/FieldKeysGeneric';
import Globals from '../constants/Globals';
import { TemplateMarketPropertyKeys } from '../templates/TemplateMarketProperties';
import { ActivityTypeGroupsMarketPropertyKeys } from '../constants/ActivityTypeGroups';
import { ConvertionDistanceUnits } from '../constants/DistanceUnits';
import { getConfigurationMarketProperty } from '../utils/WidgetUtils';
import { getDeepPropertyValue } from '../utils/WidgetUtils';
import { getConfigurationTranslationValue } from '../utils/WidgetUtils';
import { getActivityTypeTranslationCode } from './TranslationUtils';
import { getProductGroupTranslationCode } from './TranslationUtils';
import { getMapKeyByValue } from './WidgetUtils';

export const convertDealerDistance = (
  dealer: $Dealer, configuration: $Configuration, settings: Object, translations: Array<$Translation>
): ?string => {
  const dataDistanceUnit = getDeepPropertyValue(['address', 'airDistance', 'unit'], dealer) || '';
  const dataDistanceValue = getDeepPropertyValue(['address', 'airDistance', 'distance'], dealer) || '';
  const distanceUnitTranslation = mapDistanceMeasurementUnitToTranslation(dataDistanceUnit, translations) || '';

  const delimiter = getConfigurationMarketProperty(
    configuration, settings.market, TemplateMarketPropertyKeys.GEN_DISTANCE_DELIMITER
  ) || Globals.DEFAULT_DISTANCE_DELIMITER;

  if (dataDistanceValue && distanceUnitTranslation) {

    const distanceValue = dataDistanceValue.toString().indexOf('.') > -1 ?
      parseFloat(dataDistanceValue).toFixed(1) : dataDistanceValue;

    return `${ distanceValue.toString().replace('.', delimiter) } ${distanceUnitTranslation}`;
  }
  else return null;
};

export const getActivityTypesSetFromDealer = (dealer: $Dealer): Array<string> => {
  return dealer && dealer.functions
    ? Array.from(new Set(
      dealer.functions && dealer.functions.map(({ activityCode }) => activityCode))).sort() : [];
};

export const getProductGroupsSetFromDealer = (dealer: $Dealer): Array<string> => {
  return dealer && dealer.functions
    ? Array.from(new Set(
      dealer.functions && dealer.functions.map(({ productGroupCode }) => productGroupCode))).sort() : [];
};

export const getActivityTypesSetFromDealersList = (dealersList: Array<$Dealer>): Array<string> => {
  const activityTypes = new Set();
  for (const dealer of dealersList) {
    const dealerActivityTypes = getActivityTypesSetFromDealer(dealer);
    for (const dealerActivityType of dealerActivityTypes) {
      activityTypes.add(dealerActivityType);
    }
  }
  return Array.from(activityTypes).sort();
};

export const getProductGroupsSetFromDealersList = (dealersList: Array<$Dealer>): Array<string> => {
  const productGroups = new Set();
  for (const dealer of dealersList) {
    const dealerProductGroups = getProductGroupsSetFromDealer(dealer);
    for (const dealerProductGroup of dealerProductGroups) {
      productGroups.add(dealerProductGroup);
    }
  }
  return Array.from(productGroups).sort();
};

export const getActivityTypesOptions = (
  dealers: Array<$Dealer>, translations: Array<$Translation>
): $DropdownOptions => {
  const activityTypeOptions: $DropdownOptions = {
    labels: [],
    values: []
  };
  const activityTypes = getActivityTypesSetFromDealersList(dealers);
  activityTypes.map((activityType: string) => {
    const activityTypeTranslation = mapDealerActivityTypeToTranslation(translations, activityType);
    if (activityType && activityTypeTranslation) {
      activityTypeOptions.labels.push(activityTypeTranslation);
      activityTypeOptions.values.push(activityType);
    }
  });
  return activityTypeOptions;
};

export const getProductGroupOptions = (
  dealers: Array<$Dealer>, translations: Array<$Translation>
): $DropdownOptions => {
  const productGroupOptions: $DropdownOptions = {
    labels: [],
    values: []
  };
  const productGroups = getProductGroupsSetFromDealersList(dealers);
  productGroups.map((productGroup: string) => {
    const productGroupTranslation = mapDealerProductGroupToTranslation(translations, productGroup);
    if (productGroup && productGroupTranslation) {
      productGroupOptions.labels.push(productGroupTranslation);
      productGroupOptions.values.push(productGroup);
    }
  });
  return productGroupOptions;
};

export const filterDealersByActivityTypes = (
  dealersList: Array<$Dealer>, activityTypes: Array<string>, configuration: $Configuration, market: string
): Array<$Dealer> => {
  return activityTypes.length > 0 ?
    dealersList.filter((dealer: $Dealer) => {
      return activityTypes.every((activity: string) => (
        dealer.functions && dealer.functions.find((dealerFunction: $DealerFunction) => {
          const dealerActivityType = dealerFunction.activityCode;
          if (isActivityTypeGroupCode(activity, ActivityTypeGroupsMarketPropertyKeys)) {
            const marketPropertyKey = getMapKeyByValue(ActivityTypeGroupsMarketPropertyKeys, activity);
            if (marketPropertyKey) {
              const marketProperty = getConfigurationMarketProperty(configuration, market, marketPropertyKey) || [];
              return marketProperty.includes(dealerActivityType);
            }
            else return false;
          }
          else return dealerActivityType === activity;
        })
      ));
    }
  ) : dealersList;
};

export const activityTypeBelongsToActivityTypeGroup = (
  activityType: string, configuration: $Configuration, market: string, templateKey: string
): boolean => {
  const marketProperty = getConfigurationMarketProperty(configuration, market, templateKey) || [];
  return marketProperty.includes(activityType);
};

export const isActivityTypeGroupCode = (activityTypeCode: string, groupsMap: Map<string, string>) => {
  return Array.from(groupsMap.values()).includes(activityTypeCode);
};

export const filterDealersByProductGroups = (
  dealersList: Array<$Dealer>, productGroups: Array<string>
): Array<$Dealer> => {
  return productGroups.length > 0 ? dealersList.filter((dealer: $Dealer) => (
    productGroups.every((productGroup: string) => (
      dealer.functions && dealer.functions.find((dealerFunction: $DealerFunction) => (
        dealerFunction.productGroupCode === productGroup
      ))
    ))
  )) : dealersList;
};

export const getDealerFirstAvailableName = (dealer: $Dealer): string => {
  return dealer.baseInfo && (
    dealer.baseInfo.name1 || dealer.baseInfo.name2 || dealer.baseInfo.name3 ||
    dealer.baseInfo.name4 || dealer.baseInfo.name5) ||
    ''
};

export const getSuggestedDealersOptionsList = async (dmsEndpoint: string) => {
  const dms = new DMSConsumer(dmsEndpoint);
  const dealersListResponse: $AxiosXHR<Array<string>> = await dms.getDealersSuggestedList();
  let dealersOptionsList: Array<Object> = [];
  if (dealersListResponse && dealersListResponse.data) {
    dealersListResponse.data.forEach((externalId: string) => {
      dealersOptionsList.push({
        label: externalId,
        value: externalId
      });
    });
  }
  return dealersOptionsList;
};

export const mapDealerActivityTypeToTranslation = (
  translations: Array<$Translation>, activityType: string
): string => {
  const translationKey = getActivityTypeTranslationCode(activityType);
  return getConfigurationTranslationValue(translations, translationKey);
};

export const mapDealerProductGroupToTranslation = (
  translations: Array<$Translation>, productGroup: string
): string => {
  const translationKey = getProductGroupTranslationCode(productGroup);
  return getConfigurationTranslationValue(translations, translationKey);
};

export const mapDistanceMeasurementUnitToTranslation = (unit: string, translations: Array<$Translation>): ?string => {
  switch (unit) {
    case ConvertionDistanceUnits.DISTANCE_UNIT_KM:
      return getConfigurationTranslationValue(translations, FieldKeysGeneric.DISTANCE_UNIT_KM);
    case ConvertionDistanceUnits.DISTANCE_UNIT_MI:
      return getConfigurationTranslationValue(translations, FieldKeysGeneric.DISTANCE_UNIT_MI);
    case ConvertionDistanceUnits.DISTANCE_UNIT_M:
      return getConfigurationTranslationValue(translations, FieldKeysGeneric.DISTANCE_UNIT_M);
    case ConvertionDistanceUnits.DISTANCE_UNIT_YD:
      return getConfigurationTranslationValue(translations, FieldKeysGeneric.DISTANCE_UNIT_YD);
  }
};
